'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { X, Clock, BookOpen, Award, Star, Users, TrendingUp, ChevronDown, ChevronUp, Play, FileText, CheckSquare, Video, ArrowRight, CheckCircle, GraduationCap, Briefcase, MapPin,  } from 'lucide-react';

interface Lesson {
  id: string;
  title: string;
  type: 'video' | 'reading' | 'assessment' | 'assignment' | 'live';
  duration: string;
  description?: string;
}

interface CurriculumModule {
  id: string;
  number: number;
  title: string;
  description: string;
  duration: string;
  lessons: Lesson[];
}

interface FacultyLead {
  name: string;
  role: string;
  bio: string;
  image?: string;
}

interface Programme {
  id: string;
  slug: string;
  title: string;
  label: string;
  school: string;
  schoolId: string;
  duration: string;
  format: string;
  price: number;
  priceDisplay: string;
  monthlyPrice: string;
  description: string;
  modules: number;
  cpd: number;
  image: string;
  outcomes: string[];
  intakeDates: string[];
  tag?: string;
  tagColor?: string;
  comingSoon?: boolean;
  faculty?: string;
  topics?: string[];
  facultyLeads?: FacultyLead[];
  curriculumModules?: CurriculumModule[];
  enrollmentStatus?: string;
  nextIntake?: string;
  totalLessons?: number;
  weeklyHours?: string;
  level?: string;
  prerequisites?: string[];
  certificationAwarded?: string;
  longDescription?: string;
}

interface SchoolConfig {
  name: string;
  description: string;
  separator: string;
  dot: string;
  labelBg: string;
  labelBorder: string;
  cardHover: string;
  accentLine: string;
}

interface CourseCardModalProps {
  prog: Programme;
  config: SchoolConfig;
  onClose: () => void;
}

type TabId = 'overview' | 'curriculum' | 'reviews' | 'alumni';

// ── Static enrichment data ────────────────────────────────────────────────────

const reviewsByProgramme: Record<string, Array<{
  name: string;
  role: string;
  location: string;
  rating: number;
  date: string;
  text: string;
  initials: string;
  colour: string;
}>> = {
  default: [
    {
      name: 'Thandi Nkosi',
      role: 'Property Practitioner',
      location: 'Johannesburg',
      rating: 5,
      date: 'March 2026',
      text: 'This programme completely transformed how I approach my practice. The curriculum is rigorous, practical, and directly applicable to the South African market. I passed my PPRA assessment on the first attempt.',
      initials: 'TN',
      colour: 'bg-accent/15 text-accent',
    },
    {
      name: 'Sipho Dlamini',
      role: 'Estate Agent',
      location: 'Durban',
      rating: 5,
      date: 'February 2026',
      text: 'The self-paced format worked perfectly around my existing client commitments. The faculty are genuine industry practitioners — not just academics. Highly recommend to anyone serious about their career.',
      initials: 'SD',
      colour: 'bg-primary/15 text-primary',
    },
    {
      name: 'Lerato Molefe',
      role: 'Property Manager',
      location: 'Pretoria',
      rating: 4,
      date: 'January 2026',
      text: 'Excellent content depth and very well structured. The live sessions added real value — being able to ask questions directly to practitioners made a huge difference. Would have liked more case studies.',
      initials: 'LM',
      colour: 'bg-emerald-100 text-emerald-700',
    },
    {
      name: 'Ayanda Zulu',
      role: 'Investment Analyst',
      location: 'Cape Town',
      rating: 5,
      date: 'December 2025',
      text: 'REUSA\'s approach to education is genuinely different. The content is current, the platform is excellent, and the certificate carries real weight in the industry. Worth every rand.',
      initials: 'AZ',
      colour: 'bg-blue-100 text-blue-700',
    },
  ],
};

const alumniByProgramme: Record<string, Array<{
  name: string;
  role: string;
  company: string;
  location: string;
  year: string;
  achievement: string;
  initials: string;
  colour: string;
}>> = {
  default: [
    {
      name: 'Nomvula Khumalo',
      role: 'Principal Property Practitioner',
      company: 'Seeff Properties',
      location: 'Sandton',
      year: '2025',
      achievement: 'Promoted to branch principal within 8 months of completing the programme.',
      initials: 'NK',
      colour: 'bg-accent/15 text-accent',
    },
    {
      name: 'Riaan van der Berg',
      role: 'Property Development Manager',
      company: 'Growthpoint Properties',
      location: 'Cape Town',
      year: '2025',
      achievement: 'Led a R42m mixed-use development project using skills from the programme.',
      initials: 'RB',
      colour: 'bg-primary/15 text-primary',
    },
    {
      name: 'Fatima Moosa',
      role: 'Real Estate Investment Analyst',
      company: 'Redefine Properties',
      location: 'Johannesburg',
      year: '2024',
      achievement: 'Transitioned from residential sales to institutional investment analysis.',
      initials: 'FM',
      colour: 'bg-emerald-100 text-emerald-700',
    },
    {
      name: 'Bongani Sithole',
      role: 'Commercial Property Broker',
      company: 'JLL South Africa',
      location: 'Durban',
      year: '2024',
      achievement: 'Closed R18m in commercial transactions in first year post-qualification.',
      initials: 'BS',
      colour: 'bg-blue-100 text-blue-700',
    },
  ],
};

const completionRateByProgramme: Record<string, number> = {
  'q-001': 91,
  'q-002': 88,
  'q-003': 85,
  'c-001': 93,
  'c-003': 94,
  'e-001': 89,
  'e-002': 82,
  'e-003': 87,
  'e-004': 86,
  'e-005': 92,
  'e-006': 84,
  'e-007': 88,
  'c-002': 83,
  'c-004': 95,
  default: 89,
};

const avgRatingByProgramme: Record<string, number> = {
  'q-001': 4.8,
  'q-002': 4.7,
  'c-001': 4.9,
  'c-003': 4.8,
  'e-001': 4.9,
  'e-002': 4.6,
  'e-003': 4.8,
  'e-005': 4.9,
  default: 4.7,
};

const totalReviewsByProgramme: Record<string, number> = {
  'q-001': 214,
  'q-002': 187,
  'c-001': 143,
  'e-001': 98,
  'e-002': 76,
  'e-003': 112,
  default: 89,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function LessonIcon({ type }: { type: Lesson['type'] }) {
  switch (type) {
    case 'video': return <Video size={12} className="text-primary" />;
    case 'reading': return <FileText size={12} className="text-accent" />;
    case 'assessment': return <CheckSquare size={12} className="text-emerald-600" />;
    case 'assignment': return <Briefcase size={12} className="text-blue-600" />;
    case 'live': return <Play size={12} className="text-orange-500" />;
    default: return <FileText size={12} className="text-muted-foreground" />;
  }
}

function StarRating({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/30'}
        />
      ))}
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────

export default function CourseCardModal({ prog, config, onClose }: CourseCardModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [expandedModule, setExpandedModule] = useState<string | null>(null);

  const completionRate = completionRateByProgramme[prog.id] ?? completionRateByProgramme.default;
  const avgRating = avgRatingByProgramme[prog.id] ?? avgRatingByProgramme.default;
  const totalReviews = totalReviewsByProgramme[prog.id] ?? totalReviewsByProgramme.default;
  const reviews = reviewsByProgramme[prog.id] ?? reviewsByProgramme.default;
  const alumni = alumniByProgramme[prog.id] ?? alumniByProgramme.default;

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'curriculum', label: `Curriculum (${prog.modules} modules)` },
    { id: 'reviews', label: `Reviews (${totalReviews})` },
    { id: 'alumni', label: 'Alumni' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal panel */}
      <div className="relative bg-background rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* Hero image + header */}
        <div className="relative h-44 sm:h-52 flex-shrink-0 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${prog.image}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>

          {/* Tags */}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <span className={`font-sans text-[0.58rem] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-md border ${config.labelBg} ${config.labelBorder} bg-white/90`}>
              {prog.label}
            </span>
            {prog.tag && (
              <span className={`font-sans text-[0.58rem] font-bold px-2.5 py-1 rounded-md ${prog.tagColor} bg-white/90`}>
                {prog.tag}
              </span>
            )}
          </div>

          {/* Title overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
            <p className="font-sans text-[0.65rem] font-semibold text-white/70 uppercase tracking-widest mb-1">{prog.school}</p>
            <h2 className="font-serif font-bold text-lg sm:text-xl text-white leading-tight">{prog.title}</h2>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex-shrink-0 border-b border-border bg-card px-4 sm:px-6 py-3">
          <div className="grid grid-cols-4 gap-3 sm:gap-4">
            {/* Completion rate */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <TrendingUp size={12} className="text-emerald-600" />
                <span className="font-sans text-xs font-bold text-foreground">{completionRate}%</span>
              </div>
              <p className="font-sans text-[0.6rem] text-muted-foreground leading-tight">Completion</p>
            </div>
            {/* Rating */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <Star size={12} className="text-amber-400 fill-amber-400" />
                <span className="font-sans text-xs font-bold text-foreground">{avgRating}</span>
              </div>
              <p className="font-sans text-[0.6rem] text-muted-foreground leading-tight">{totalReviews} reviews</p>
            </div>
            {/* Duration */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <Clock size={12} className="text-primary" />
                <span className="font-sans text-xs font-bold text-foreground">{prog.duration}</span>
              </div>
              <p className="font-sans text-[0.6rem] text-muted-foreground leading-tight">{prog.weeklyHours ?? prog.format}</p>
            </div>
            {/* CPD */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <Award size={12} className="text-accent" />
                <span className="font-sans text-xs font-bold text-foreground">{prog.cpd}</span>
              </div>
              <p className="font-sans text-[0.6rem] text-muted-foreground leading-tight">CPD points</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 border-b border-border bg-card px-4 sm:px-6">
          <div className="flex gap-0 overflow-x-auto scrollbar-hide -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`font-sans text-xs font-semibold whitespace-nowrap px-3 sm:px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content — scrollable */}
        <div className="flex-1 overflow-y-auto">

          {/* ── OVERVIEW TAB ── */}
          {activeTab === 'overview' && (
            <div className="p-4 sm:p-6 space-y-6">
              {/* Description */}
              <div>
                <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                  {prog.longDescription ?? prog.description}
                </p>
              </div>

              {/* Completion rate visual */}
              <div className="bg-secondary/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={14} className="text-emerald-600" />
                    <span className="font-sans text-sm font-semibold text-foreground">Programme Completion Rate</span>
                  </div>
                  <span className="font-sans text-sm font-bold text-emerald-600">{completionRate}%</span>
                </div>
                <div className="h-2 bg-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                    style={{ width: `${completionRate}%` }}
                  />
                </div>
                <p className="font-sans text-[0.65rem] text-muted-foreground mt-1.5">
                  Based on enrolled learners who completed all assessments and received their certificate.
                </p>
              </div>

              {/* Learning outcomes */}
              <div>
                <h4 className="font-serif font-semibold text-sm text-foreground mb-3">What You Will Achieve</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {prog.outcomes.map((outcome, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <CheckCircle size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="font-sans text-xs text-muted-foreground leading-relaxed">{outcome}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Programme details */}
              <div className="grid grid-cols-2 gap-3">
                {prog.level && (
                  <div className="bg-card border border-border rounded-xl p-3">
                    <p className="font-sans text-[0.6rem] text-muted-foreground uppercase tracking-wider mb-1">Level</p>
                    <p className="font-sans text-xs font-semibold text-foreground">{prog.level}</p>
                  </div>
                )}
                <div className="bg-card border border-border rounded-xl p-3">
                  <p className="font-sans text-[0.6rem] text-muted-foreground uppercase tracking-wider mb-1">Format</p>
                  <p className="font-sans text-xs font-semibold text-foreground">{prog.format}</p>
                </div>
                {prog.totalLessons && (
                  <div className="bg-card border border-border rounded-xl p-3">
                    <p className="font-sans text-[0.6rem] text-muted-foreground uppercase tracking-wider mb-1">Total Lessons</p>
                    <p className="font-sans text-xs font-semibold text-foreground">{prog.totalLessons} lessons</p>
                  </div>
                )}
                <div className="bg-card border border-border rounded-xl p-3">
                  <p className="font-sans text-[0.6rem] text-muted-foreground uppercase tracking-wider mb-1">Next Intake</p>
                  <p className="font-sans text-xs font-semibold text-foreground">{prog.nextIntake ?? prog.intakeDates[0]}</p>
                </div>
              </div>

              {/* Prerequisites */}
              {prog.prerequisites && prog.prerequisites.length > 0 && (
                <div>
                  <h4 className="font-serif font-semibold text-sm text-foreground mb-2">Prerequisites</h4>
                  <ul className="space-y-1.5">
                    {prog.prerequisites.map((req, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 flex-shrink-0" />
                        <span className="font-sans text-xs text-muted-foreground">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Faculty leads */}
              {prog.facultyLeads && prog.facultyLeads.length > 0 && (
                <div>
                  <h4 className="font-serif font-semibold text-sm text-foreground mb-3">Programme Faculty</h4>
                  <div className="space-y-3">
                    {prog.facultyLeads.map((lead, i) => (
                      <div key={i} className="flex items-start gap-3 bg-card border border-border rounded-xl p-3">
                        {lead.image ? (
                          <img
                            src={lead.image}
                            alt={lead.name}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="font-sans text-xs font-bold text-primary">
                              {lead.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-sans text-xs font-semibold text-foreground">{lead.name}</p>
                          <p className="font-sans text-[0.65rem] text-accent mb-1">{lead.role}</p>
                          <p className="font-sans text-[0.65rem] text-muted-foreground leading-relaxed">{lead.bio}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── CURRICULUM TAB ── */}
          {activeTab === 'curriculum' && (
            <div className="p-4 sm:p-6">
              {prog.curriculumModules && prog.curriculumModules.length > 0 ? (
                <div className="space-y-2">
                  {prog.curriculumModules.map((mod) => (
                    <div key={mod.id} className="border border-border rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedModule(expandedModule === mod.id ? null : mod.id)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-secondary/40 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${config.labelBg} border ${config.labelBorder}`}>
                            <span className="font-sans text-[0.6rem] font-bold">{mod.number}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-sans text-xs font-semibold text-foreground leading-snug">{mod.title}</p>
                            <p className="font-sans text-[0.6rem] text-muted-foreground">{mod.lessons.length} lessons · {mod.duration}</p>
                          </div>
                        </div>
                        {expandedModule === mod.id
                          ? <ChevronUp size={14} className="text-muted-foreground flex-shrink-0" />
                          : <ChevronDown size={14} className="text-muted-foreground flex-shrink-0" />
                        }
                      </button>

                      {expandedModule === mod.id && (
                        <div className="border-t border-border bg-secondary/20 px-4 pb-3 pt-2">
                          <p className="font-sans text-[0.65rem] text-muted-foreground mb-3 leading-relaxed">{mod.description}</p>
                          <div className="space-y-1.5">
                            {mod.lessons.map((lesson) => (
                              <div key={lesson.id} className="flex items-center gap-2.5 py-1">
                                <LessonIcon type={lesson.type} />
                                <span className="font-sans text-xs text-foreground flex-1 leading-snug">{lesson.title}</span>
                                <span className="font-sans text-[0.6rem] text-muted-foreground flex-shrink-0">{lesson.duration}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <BookOpen size={32} className="text-muted-foreground/30 mx-auto mb-3" />
                  <p className="font-sans text-sm text-muted-foreground">Detailed curriculum available upon enquiry.</p>
                  <p className="font-sans text-xs text-muted-foreground mt-1">This programme covers {prog.modules} modules across {prog.duration}.</p>
                </div>
              )}

              {/* Certification */}
              {prog.certificationAwarded && (
                <div className="mt-5 bg-accent/5 border border-accent/20 rounded-xl p-4 flex items-start gap-3">
                  <GraduationCap size={18} className="text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-sans text-xs font-semibold text-foreground mb-0.5">Certification Awarded</p>
                    <p className="font-sans text-xs text-muted-foreground">{prog.certificationAwarded}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── REVIEWS TAB ── */}
          {activeTab === 'reviews' && (
            <div className="p-4 sm:p-6 space-y-5">
              {/* Summary */}
              <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-5">
                <div className="text-center flex-shrink-0">
                  <p className="font-serif text-4xl font-bold text-foreground">{avgRating}</p>
                  <StarRating rating={avgRating} size={14} />
                  <p className="font-sans text-[0.6rem] text-muted-foreground mt-1">{totalReviews} reviews</p>
                </div>
                <div className="flex-1 space-y-1.5">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const pct = star === 5 ? 68 : star === 4 ? 22 : star === 3 ? 7 : star === 2 ? 2 : 1;
                    return (
                      <div key={star} className="flex items-center gap-2">
                        <span className="font-sans text-[0.6rem] text-muted-foreground w-3">{star}</span>
                        <Star size={9} className="text-amber-400 fill-amber-400 flex-shrink-0" />
                        <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-sans text-[0.6rem] text-muted-foreground w-6 text-right">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Completion rate callout */}
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <TrendingUp size={16} className="text-emerald-600 flex-shrink-0" />
                <p className="font-sans text-xs text-emerald-800">
                  <span className="font-bold">{completionRate}% of enrolled learners</span> complete this programme and receive their certificate.
                </p>
              </div>

              {/* Individual reviews */}
              <div className="space-y-4">
                {reviews.map((review, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-sans text-xs font-bold ${review.colour}`}>
                        {review.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-sans text-xs font-semibold text-foreground">{review.name}</p>
                          <span className="font-sans text-[0.6rem] text-muted-foreground flex-shrink-0">{review.date}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <StarRating rating={review.rating} size={11} />
                          <span className="font-sans text-[0.6rem] text-muted-foreground">{review.role} · {review.location}</span>
                        </div>
                      </div>
                    </div>
                    <p className="font-sans text-xs text-muted-foreground leading-relaxed">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ALUMNI TAB ── */}
          {activeTab === 'alumni' && (
            <div className="p-4 sm:p-6 space-y-5">
              <div>
                <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                  REUSA graduates go on to lead in South Africa's most respected property organisations. Here are some of the professionals who have completed this programme.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {alumni.map((person, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-sans text-xs font-bold ${person.colour}`}>
                        {person.initials}
                      </div>
                      <div className="min-w-0">
                        <p className="font-sans text-xs font-semibold text-foreground">{person.name}</p>
                        <p className="font-sans text-[0.65rem] text-accent font-medium">{person.role}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Briefcase size={10} className="text-muted-foreground" />
                          <span className="font-sans text-[0.6rem] text-muted-foreground">{person.company}</span>
                          <MapPin size={10} className="text-muted-foreground ml-1" />
                          <span className="font-sans text-[0.6rem] text-muted-foreground">{person.location}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-secondary/50 rounded-lg px-3 py-2">
                      <p className="font-sans text-[0.65rem] text-muted-foreground leading-relaxed italic">"{person.achievement}"</p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1">
                        <GraduationCap size={10} className="text-muted-foreground" />
                        <span className="font-sans text-[0.6rem] text-muted-foreground">Class of {person.year}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-card border border-border rounded-xl p-3 text-center">
                  <p className="font-serif text-xl font-bold text-primary">94%</p>
                  <p className="font-sans text-[0.6rem] text-muted-foreground leading-tight mt-0.5">employed in property within 6 months</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-3 text-center">
                  <p className="font-serif text-xl font-bold text-accent">3.2×</p>
                  <p className="font-sans text-[0.6rem] text-muted-foreground leading-tight mt-0.5">average salary increase post-qualification</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Users size={12} className="text-emerald-600" />
                    <p className="font-serif text-xl font-bold text-emerald-600">1,200+</p>
                  </div>
                  <p className="font-sans text-[0.6rem] text-muted-foreground leading-tight mt-0.5">alumni across South Africa</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sticky CTA footer */}
        <div className="flex-shrink-0 border-t border-border bg-card px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <span className="font-sans text-[0.65rem] text-muted-foreground block">Programme fee</span>
              <div className="flex items-baseline gap-2">
                <span className="font-serif font-bold text-xl text-primary">{prog.priceDisplay}</span>
                <span className="font-sans text-xs text-muted-foreground">or {prog.monthlyPrice}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="font-sans text-xs font-semibold px-4 py-2.5 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
              >
                Close
              </button>
              {!prog.comingSoon && (
                <Link
                  href={`/programme/${prog.slug}`}
                  onClick={onClose}
                  className="flex items-center gap-1.5 bg-primary text-white text-xs font-semibold px-5 py-2.5 rounded-xl hover:bg-green-light transition-colors"
                >
                  Apply Now
                  <ArrowRight size={13} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
